// src/pages/CheckoutSuccess.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Download, Mail, ArrowRight, Calendar, Clock } from 'lucide-react';
import paymentService from '../services/payment-service';

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [purchases, setPurchases] = useState([]);
  
  // Get order data from navigation state
  const orderData = location.state || {
    orderNumber: 'DF-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
    type: 'course',
    item: {
      title: 'Your Purchase',
      instructor: 'Divine Frameworks',
      price: 0
    }
  };

  useEffect(() => {
    // Load purchase history to show the latest purchase
    const history = paymentService.getPurchaseHistory();
    setPurchases(history);
    
    // Confetti effect (optional fun addition!)
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    // Only run confetti if window.confetti exists (you can add a confetti library later)
    if (window.confetti) {
      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }
        const particleCount = 50 * (timeLeft / duration);
        window.confetti(Object.assign({}, defaults, { 
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        }));
        window.confetti(Object.assign({}, defaults, { 
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        }));
      }, 250);
    }
  }, []);

  const isSession = orderData.type === 'session';
  const latestPurchase = purchases[purchases.length - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Success Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-lg text-slate-600">
              Thank you for your purchase. Your order has been confirmed.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Order Confirmation Box */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="border-b border-slate-200 pb-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-slate-600 mb-1">Order Number</p>
                <p className="text-2xl font-bold text-slate-900">{orderData.orderNumber}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600 mb-1">Date</p>
                <p className="text-lg font-semibold text-slate-900">
                  {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Test Mode Indicator */}
            {paymentService.isTestMode() && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  🧪 <strong>Test Purchase:</strong> This was a simulated payment. No real charges occurred.
                </p>
              </div>
            )}
          </div>

          {/* Purchase Details */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              {isSession ? 'Session Details' : 'Course Details'}
            </h2>
            
            {latestPurchase && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center text-2xl">
                    {isSession ? '🧘' : '📚'}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">
                      {latestPurchase.productName || latestPurchase.planName || orderData.item.title}
                    </h3>
                    <p className="text-slate-600 mt-1">
                      Amount Paid: {paymentService.formatPrice(latestPurchase.amount)}
                    </p>
                    {isSession && (
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date().toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          2:00 PM (Sample time)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* What's Next */}
          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">What's Next?</h2>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Check Your Email</p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    We've sent a confirmation email with your receipt and access details.
                  </p>
                </div>
              </div>

              {isSession ? (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Calendar className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Session Confirmed</p>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Your practitioner will reach out within 24 hours to confirm your session time.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Download className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Access Your Course</p>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Your course is now available in your learning dashboard.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600">💬</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Need Help?</p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Our support team is here to help. Contact us anytime at support@divineframeworks.com
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            {isSession ? (
              <>
                <button
                  onClick={() => navigate('/messages')}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Check Messages
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                >
                  Go to Dashboard
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/dashboard/practitioner')}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/directory')}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
                >
                  Find Practitioners
                </button>
              </>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">
            🎉 You're Part of Something Special!
          </h3>
          <p className="text-blue-800 text-sm">
            Welcome to the Divine Frameworks community! You've joined thousands of seekers and learners 
            on their spiritual journey. We're excited to support your growth and transformation.
          </p>
        </div>

        {/* Purchase History Preview */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/payment-test')}
            className="text-sm text-slate-600 hover:text-slate-900 underline"
          >
            View Full Purchase History ({purchases.length} {purchases.length === 1 ? 'item' : 'items'})
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;